import { cmsApi } from './api'
import { storeLocal } from './images'
import { pointFromText, suggestHandle } from './places'
import { firstInvalidLink, normalizeHandle, validHandle } from '../components/catalogForm'
import { AMENITY_KEYS, EMPTY_AMENITIES } from '../components/AmenityPicker'
import type { PlaceAmenities, PlaceEntry, PlaceFaq, PlaceForm, PlaceProfile, PlaceRecord, Producer, ProducerRecord, ProfileFields, ProfileForm } from '../types'

// A place's and a profile's forms (PRD.md, item 98): the same in Lugares,
// Perfis and inside an event, created and saved the same way.

export const EMPTY_PROFILE: ProfileFields = {
  name: '', handle: null, bio: null, image_url: null, image_url_source: null,
  instagram_url: null, tiktok_url: null, website_url: null,
}

// Every place in the catalog is in Rio de Janeiro so far.
export const newPlaceForm = (name = ''): PlaceForm => ({
  name, short_name: null, address: null, city: null, state: 'RJ', coordinates: null,
  amenities: EMPTY_AMENITIES, faqs: [],
  profile: { mode: 'create', link_id: null, fields: { ...EMPTY_PROFILE, name, handle: suggestHandle(name) || null } },
})

export const newProfileForm = (name = '', kind: ProfileForm['kind'] = 'producer'): ProfileForm => ({
  ...EMPTY_PROFILE, name, handle: suggestHandle(name) || null, kind, place_id: null,
})

// A saved place as its form opens. Without a profile, the one it would create
// starts with the photo, text and links the place had before they moved to
// profiles.
export const placeFormOf = ({ place, faqs = [] }: PlaceEntry): PlaceForm => ({
  name: place.name ?? '', short_name: place.short_name ?? null, address: place.address ?? null,
  city: place.city ?? null, state: place.state ?? null, coordinates: place.coordinates ?? null,
  amenities: Object.fromEntries(AMENITY_KEYS.map((key) => [key, place[key] ?? null])) as PlaceAmenities,
  faqs,
  profile: {
    mode: 'none', link_id: null,
    fields: {
      name: place.name ?? '', handle: suggestHandle(place.name ?? '') || null, bio: place.bio ?? null,
      image_url: place.image_url ?? null, image_url_source: null, instagram_url: place.instagram_url ?? null,
      tiktok_url: place.tiktok_url ?? null, website_url: place.website_url ?? null,
    },
  },
})

export const profileFormOf = (producer: ProducerRecord): ProfileForm => ({
  name: producer.name ?? '', handle: producer.handle ?? null, bio: producer.bio ?? null,
  image_url: producer.image_url ?? null, image_url_source: producer.image_url_source ?? null,
  instagram_url: producer.instagram_url ?? null, tiktok_url: producer.tiktok_url ?? null,
  website_url: producer.website_url ?? null, kind: producer.kind ?? 'producer', place_id: producer.place_id ?? null,
})

// Whether anything was typed in a new place or profile.
export const placeFilled = (form: PlaceForm) => JSON.stringify(form) !== JSON.stringify(newPlaceForm(form.name)) || Boolean(form.name.trim())
export const profileFilled = (form: ProfileForm) => JSON.stringify(form) !== JSON.stringify(newProfileForm(form.name, form.kind)) || Boolean(form.name.trim())

// Questions in view, in their order; the removed ones wait at the end.
export const visibleFaqs = (faqs: PlaceFaq[]) => faqs.filter((faq) => faq.status !== 'deleted')

export const newPlaceFaq = (placeId: string, sortOrder: number): PlaceFaq => ({
  id: crypto.randomUUID(), place_id: placeId, question: '', answer: '', sort_order: sortOrder, status: 'published',
})

// The questions an editor changed against the saved ones: new, edited,
// moved or removed, each in the order now shown; trimmed to be saved, as
// typed while editing.
export function changedFaqs(current: PlaceFaq[], saved: PlaceFaq[], trim = true) {
  const before = new Map(saved.map((faq) => [faq.id, faq]))
  const shown = visibleFaqs(current).map((faq, index) => ({
    ...faq, question: trim ? faq.question.trim() : faq.question, answer: trim ? faq.answer.trim() : faq.answer, sort_order: index,
  }))
  const removed = current.filter((faq) => faq.status === 'deleted' && before.has(faq.id))
  return [...shown, ...removed].filter((faq) => {
    const previous = before.get(faq.id)
    return !previous || previous.question !== faq.question || previous.answer !== faq.answer
      || previous.sort_order !== faq.sort_order || previous.status !== faq.status
  })
}

const HANDLE_RULE = 'O @ usa de 3 a 30 letras minúsculas, números, ponto ou sublinhado, com ao menos uma letra.'

export function profileProblem(fields: ProfileFields) {
  if (!fields.name.trim()) return 'Informe o nome do perfil.'
  const handle = normalizeHandle(fields.handle ?? '')
  if (!handle) return 'Informe o @ do perfil.'
  if (!validHandle(handle)) return HANDLE_RULE
  const link = firstInvalidLink(fields, { image_url_source: 'Foto', instagram_url: 'Instagram', tiktok_url: 'TikTok', website_url: 'Site' })
  return link ? `Use um link completo, começando com https:// (${link}).` : ''
}

export function placeProblem(form: PlaceForm, hasProfile = false) {
  if (!form.name.trim()) return 'Informe o nome do lugar.'
  if ((form.short_name ?? '').trim().length > 40) return 'O nome curto passa de 40 caracteres.'
  const coordinates = (form.coordinates ?? '').trim()
  if (coordinates && !pointFromText(coordinates)) {
    return /^https?:\/\//i.test(coordinates)
      ? 'Esse link não traz um ponto. No Google Maps, clique com o botão direito no lugar e copie os números.'
      : 'Coordenadas no formato -22.9068, -43.1729.'
  }
  for (const faq of visibleFaqs(form.faqs)) {
    if (!faq.question.trim()) return 'Informe a pergunta em todas as perguntas do lugar.'
    if (!faq.answer.trim()) return 'Informe a resposta em todas as perguntas do lugar.'
  }
  if (hasProfile) return ''
  if (form.profile.mode === 'link' && !form.profile.link_id) return 'Escolha o perfil a vincular, ou outra opção em Perfil do lugar.'
  return form.profile.mode === 'create' ? profileProblem(form.profile.fields) : ''
}

const profileRecord = async (fields: ProfileFields) => ({
  name: fields.name.trim(),
  handle: normalizeHandle(fields.handle ?? ''),
  bio: fields.bio?.trim() || null,
  image_url: await storeLocal(fields.image_url, 'producer'),
  image_url_source: fields.image_url_source?.trim() || null,
  instagram_url: fields.instagram_url?.trim() || null,
  tiktok_url: fields.tiktok_url?.trim() || null,
  website_url: fields.website_url?.trim() || null,
})

// A form's photo in the Guve, for a draft that must open on another day.
export async function storedPlaceForm(form: PlaceForm): Promise<PlaceForm> {
  const image_url = await storeLocal(form.profile.fields.image_url, 'producer')
  return image_url === form.profile.fields.image_url ? form
    : { ...form, profile: { ...form.profile, fields: { ...form.profile.fields, image_url } } }
}

export async function storedProfileForm(form: ProfileForm): Promise<ProfileForm> {
  const image_url = await storeLocal(form.image_url, 'producer')
  return image_url === form.image_url ? form : { ...form, image_url }
}

// Saves a place from its form: the place, its profile created or linked, what
// it offers and its questions. A saved place sends only what changed.
export async function savePlaceForm(form: PlaceForm, options: {
  placeId?: string | null
  saved?: PlaceForm | null
  // The place's own profile, when it has one.
  profile?: PlaceProfile | null
  producers: Producer[]
  draftId?: string | null
}): Promise<{ place: PlaceRecord; profile: Pick<ProducerRecord, 'id' | 'name' | 'handle'> | null }> {
  const { placeId = null, saved = null, producers, draftId } = options
  const place: Partial<PlaceRecord> = {
    ...(placeId ? { id: placeId } : {}),
    name: form.name.trim(), short_name: form.short_name?.trim() || null, address: form.address?.trim() || null,
    city: form.city?.trim() || null, state: form.state || null,
    coordinates: pointFromText(form.coordinates ?? '') ?? (form.coordinates?.trim() || null),
  }
  const faqs = changedFaqs(form.faqs, saved?.faqs ?? [])
  const creating = !options.profile && form.profile.mode === 'create'
  const fields = creating ? await profileRecord(form.profile.fields) : null
  // A new place and its new profile go together, or neither.
  const together = !placeId && fields
  const result = await cmsApi.savePlace(place, { profile: together || undefined, faqs, draftId })
  const id = result.place.id
  let profile: Pick<ProducerRecord, 'id' | 'name' | 'handle'> | null = options.profile ?? result.producer ?? null
  // A saved place's new profile, or one that exists, becoming the place's.
  if (!profile && fields && !together) {
    profile = (await cmsApi.saveProducer({ ...fields, kind: 'venue', place_id: id })).producer
  } else if (!profile && form.profile.mode === 'link' && form.profile.link_id) {
    const chosen = producers.find((item) => item.id === form.profile.link_id)
    if (!chosen) throw new Error('O perfil escolhido não existe mais.')
    profile = (await cmsApi.saveProducer({ id: chosen.id, name: chosen.name, handle: chosen.handle, kind: 'venue', place_id: id })).producer
  }
  const changed = AMENITY_KEYS.filter((key) => form.amenities[key] !== (saved?.amenities ?? EMPTY_AMENITIES)[key])
  if (changed.length) {
    await cmsApi.updatePlaceAmenities(id, Object.fromEntries(changed.map((key) => [key, form.amenities[key]])))
  }
  return { place: result.place, profile: profile && { id: profile.id, name: profile.name, handle: profile.handle } }
}

export async function saveProfileForm(form: ProfileForm, options: { producerId?: string | null; draftId?: string | null } = {}) {
  const { producer } = await cmsApi.saveProducer({
    ...(options.producerId ? { id: options.producerId } : {}),
    ...(await profileRecord(form)),
    kind: form.kind,
    // Only a venue points to a place.
    place_id: form.kind === 'venue' ? form.place_id : null,
  }, options.draftId)
  return producer
}
